/**
 * Firebase Settings Service
 * Handles Firestore operations for site-wide settings
 */
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';
import type {
  GeneralSettings,
  HomeContent,
  AboutContent,
  ContactContent,
  HeaderSettings,
  FooterSettings,
} from '@/types/settings.types';
import {
  APP_NAME,
  APP_DESCRIPTION,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  WHATSAPP_NUMBER,
  MAX_CART_ITEMS,
  MIN_ORDER_VALUE,
  FREE_SHIPPING_THRESHOLD,
  DEFAULT_SHIPPING_COST,
  GST_RATE,
  SOCIAL_LINKS,
} from '@/config/constants';
import { contactData } from '@/data/contact';
import { aboutData } from '@/data/about';
import { ROUTES } from '@/config/routes';

// Document IDs within the settings collection
const DOCS = {
  GENERAL: 'general',
  HOME: 'home',
  ABOUT: 'about',
  CONTACT: 'contact',
  HEADER: 'header',
  FOOTER: 'footer',
} as const;

// ==========================================
// Generic helpers
// ==========================================

async function getSettingsDoc<T>(docId: string): Promise<T | null> {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.SETTINGS, docId));
    if (snap.exists()) {
      return snap.data() as T;
    }
    return null;
  } catch (error) {
    console.error(`Error reading settings/${docId}:`, error);
    return null;
  }
}

async function updateSettingsDoc(docId: string, data: Record<string, unknown>): Promise<void> {
  try {
    await setDoc(doc(db, COLLECTIONS.SETTINGS, docId), {
      ...data,
      updatedAt: Timestamp.now(),
    }, { merge: true });
  } catch (error) {
    console.error(`Error updating settings/${docId}:`, error);
    throw new Error(`Failed to update ${docId} settings`);
  }
}

// ==========================================
// Typed getters
// ==========================================

export const getGeneralSettings = () => getSettingsDoc<GeneralSettings>(DOCS.GENERAL);
export const getHomeContent = () => getSettingsDoc<HomeContent>(DOCS.HOME);
export const getAboutContent = () => getSettingsDoc<AboutContent>(DOCS.ABOUT);
export const getContactContent = () => getSettingsDoc<ContactContent>(DOCS.CONTACT);
export const getHeaderSettings = () => getSettingsDoc<HeaderSettings>(DOCS.HEADER);
export const getFooterSettings = () => getSettingsDoc<FooterSettings>(DOCS.FOOTER);

// ==========================================
// Typed updaters
// ==========================================

export const updateGeneralSettings = (data: Partial<GeneralSettings>) =>
  updateSettingsDoc(DOCS.GENERAL, data as Record<string, unknown>);

export const updateHomeContent = (data: Partial<HomeContent>) =>
  updateSettingsDoc(DOCS.HOME, data as Record<string, unknown>);

export const updateAboutContent = (data: Partial<AboutContent>) =>
  updateSettingsDoc(DOCS.ABOUT, data as Record<string, unknown>);

export const updateContactContent = (data: Partial<ContactContent>) =>
  updateSettingsDoc(DOCS.CONTACT, data as Record<string, unknown>);

export const updateHeaderSettings = (data: Partial<HeaderSettings>) =>
  updateSettingsDoc(DOCS.HEADER, data as Record<string, unknown>);

export const updateFooterSettings = (data: Partial<FooterSettings>) =>
  updateSettingsDoc(DOCS.FOOTER, data as Record<string, unknown>);

// ==========================================
// One-time patch: fix footer link paths
// ==========================================

const FOOTER_LINKS_PATCH_KEY = 'sunvivo_footer_links_patched_v2';

export const patchFooterLinks = async (): Promise<void> => {
  if (typeof window !== 'undefined' && localStorage.getItem(FOOTER_LINKS_PATCH_KEY)) {
    return;
  }

  try {
    await updateFooterSettings({
      quickLinks: [
        { label: 'Home', path: ROUTES.HOME },
        { label: 'All Products', path: ROUTES.PRODUCTS },
        { label: 'About Us', path: ROUTES.ABOUT },
        { label: 'Contact Us', path: ROUTES.CONTACT },
      ],
      policyLinks: [
        { label: 'Privacy Policy', path: ROUTES.PRIVACY },
        { label: 'Terms of Service', path: ROUTES.TERMS },
        { label: 'Shipping Policy', path: ROUTES.SHIPPING },
        { label: 'Return Policy', path: ROUTES.RETURNS },
        { label: 'FAQ', path: `${ROUTES.CONTACT}#faq` },
      ],
    });

    if (typeof window !== 'undefined') {
      localStorage.setItem(FOOTER_LINKS_PATCH_KEY, 'true');
    }
  } catch (error) {
    console.error('Failed to patch footer links:', error);
  }
};

// ==========================================
// Migration — seeds Firestore from hardcoded data
// ==========================================

export const migrateAllSettings = async (): Promise<number> => {
  let migrated = 0;

  const generalData: GeneralSettings = {
    storeInfo: {
      name: APP_NAME,
      legalName: contactData.company.legalName,
      tagline: contactData.company.tagline,
      description: APP_DESCRIPTION,
      foundedYear: contactData.company.foundedYear,
    },
    businessRules: {
      minOrderValue: MIN_ORDER_VALUE,
      freeShippingThreshold: FREE_SHIPPING_THRESHOLD,
      defaultShippingCost: DEFAULT_SHIPPING_COST,
      gstRate: GST_RATE,
      maxCartItems: MAX_CART_ITEMS,
    },
    socialLinks: {
      facebook: SOCIAL_LINKS.facebook,
      instagram: SOCIAL_LINKS.instagram,
      twitter: SOCIAL_LINKS.twitter,
      linkedin: SOCIAL_LINKS.linkedin,
      youtube: 'https://youtube.com/@sunvivo',
      whatsapp: `https://wa.me/${WHATSAPP_NUMBER}`,
    },
    seo: {
      siteTitle: `${APP_NAME} - ${APP_DESCRIPTION}`,
      metaDescription: 'Your trusted partner for premium dental equipment, instruments, and consumables. Serving dental professionals across India.',
      metaKeywords: 'dental equipment, dental supplies, dental instruments, handpieces, sterilization, dental consumables, India',
    },
    notifications: {
      whatsappEnabled: true,
      emailEnabled: true,
      orderConfirmationEnabled: true,
      shippingUpdateEnabled: true,
    },
  };
  await updateSettingsDoc(DOCS.GENERAL, generalData as unknown as Record<string, unknown>);
  migrated++;

  const homeData: HomeContent = {
    hero: {
      tag: 'Premium Dental Equipment',
      title: 'Quality Dental Supplies',
      highlight: 'For Professionals',
      description: 'Discover our extensive range of dental instruments, handpieces, sterilization equipment, and consumables from trusted brands.',
      primaryCta: 'Shop Now',
      secondaryCta: 'Learn More',
    },
    featureHighlights: [
      { icon: 'FiTruck', title: 'Free Shipping', description: 'On orders above ₹5,000' },
      { icon: 'FiShield', title: 'Quality Assured', description: 'Certified products only' },
      { icon: 'FiHeadphones', title: '24/7 Support', description: 'Dedicated assistance' },
      { icon: 'FiRefreshCw', title: 'Easy Returns', description: 'Hassle-free policy' },
    ],
    sectionTitles: {
      featured: { title: 'Featured Products', subtitle: 'Discover our best-selling dental equipment and supplies' },
      categories: { title: 'Shop by Category', subtitle: 'Browse our wide range of dental supplies organized by category' },
      brands: { title: 'Shop by Brand', subtitle: 'Trusted brands delivering quality dental products' },
      newArrivals: { title: 'New Arrivals', subtitle: 'Check out the latest additions to our catalog' },
    },
    cta: {
      title: 'Ready to Upgrade Your Practice?',
      description: 'Browse our complete catalog of dental equipment and supplies. Quality products at competitive prices with dedicated support.',
      primaryBtn: 'Browse Products',
      secondaryBtn: 'Contact Us',
    },
  };
  await updateSettingsDoc(DOCS.HOME, homeData as unknown as Record<string, unknown>);
  migrated++;

  const aboutDataMigrate: AboutContent = {
    company: { ...aboutData.company },
    stats: aboutData.stats.map(s => ({ ...s })),
    timeline: aboutData.timeline.map(t => ({ ...t })),
    values: aboutData.values.map(v => ({ ...v })),
    team: aboutData.team.map(t => ({ ...t })),
    certifications: aboutData.certifications.map(c => ({ ...c })),
    partners: aboutData.partners.map(p => ({ ...p })),
    whyChooseUs: aboutData.whyChooseUs.map(w => ({ ...w })),
  };
  await updateSettingsDoc(DOCS.ABOUT, aboutDataMigrate as unknown as Record<string, unknown>);
  migrated++;

  const contactMigrate: ContactContent = {
    contactMethods: contactData.contactMethods.map(c => ({ ...c })),
    addresses: contactData.addresses.map(a => ({ ...a })),
    businessHours: contactData.businessHours.map(b => ({ ...b })),
    supportCategories: contactData.supportCategories.map(s => ({ ...s })),
    faqs: contactData.faqs.map(f => ({ ...f })),
  };
  await updateSettingsDoc(DOCS.CONTACT, contactMigrate as unknown as Record<string, unknown>);
  migrated++;

  const headerData: HeaderSettings = {
    topBarText: `Free shipping on orders over ₹${FREE_SHIPPING_THRESHOLD.toLocaleString('en-IN')}`,
    logoText: { brand: 'SUNVIVO', tagline: 'DENTAL' },
    showTopBar: true,
  };
  await updateSettingsDoc(DOCS.HEADER, headerData as unknown as Record<string, unknown>);
  migrated++;

  const footerData: FooterSettings = {
    brand: {
      name: 'SUNVIVO',
      tagline: 'DENTAL',
      description: 'Your trusted partner for premium dental equipment and supplies. Quality products at competitive prices with dedicated support.',
    },
    contactInfo: {
      address: `${contactData.addresses[0].addressLine1}, ${contactData.addresses[0].city}, ${contactData.addresses[0].state} ${contactData.addresses[0].pincode}, ${contactData.addresses[0].country}`,
      phone: SUPPORT_PHONE,
      email: SUPPORT_EMAIL,
    },
    quickLinks: [
      { label: 'Home', path: ROUTES.HOME },
      { label: 'All Products', path: ROUTES.PRODUCTS },
      { label: 'About Us', path: ROUTES.ABOUT },
      { label: 'Contact Us', path: ROUTES.CONTACT },
    ],
    policyLinks: [
      { label: 'Privacy Policy', path: ROUTES.PRIVACY },
      { label: 'Terms of Service', path: ROUTES.TERMS },
      { label: 'Shipping Policy', path: ROUTES.SHIPPING },
      { label: 'Return Policy', path: ROUTES.RETURNS },
      { label: 'FAQ', path: `${ROUTES.CONTACT}#faq` },
    ],
    paymentMethods: ['VISA', 'MC', 'UPI', 'COD'],
    copyrightText: `© ${new Date().getFullYear()} Sunvivo. All rights reserved.`,
  };
  await updateSettingsDoc(DOCS.FOOTER, footerData as unknown as Record<string, unknown>);
  migrated++;

  return migrated;
};
